// useLeaseLifecycle — isolates the lease-lifecycle API contract (Phase F send /
// cancel / revise / refresh / addendum / came-of-age to-dos / test-lease).
//
// Per the file-ownership split for this work, all server calls for the new
// lifecycle features live here (not in lib/, which other agents own). Keeping
// every path + body shape in one module means contract drift with the backend
// agent is reconciled in a single place.
//
// Reconciled against the backend's ACTUAL contract (seam fix A2):
//   - GET /api/applications: `leaseStatus` is an OBJECT (ComputedLeaseStatus),
//     NOT a string. We display `label` and key UI logic off `status`.
//   - Revise is GROUP-level: POST /api/lease-drafts/:groupId/revise { action }.
//   - Lease send carries NO documentType (lease only).
//   - Addendum send is a separate route: POST /api/adoption-addendum/:groupId/send.
//   - Test-send: POST /api/lease-drafts/test-send.
//
// The shared `api` helper (apps/web/src/lib/api.ts) provides auth + 401 refresh.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Lease status — server-computed OBJECT per application group (§1).
// ---------------------------------------------------------------------------

/**
 * The status enum the backend's computeLeaseStatus emits (status field). The
 * UI keys its logic off this string. 'Signing' is the "X/Y signed" family;
 * counts ride on signedCount/totalCount.
 */
export type LeaseStatusValue =
  | 'New Lease'
  | 'Awaiting Applicant Details'
  | 'Resume Draft'
  | 'Lease Generated'
  | 'Signing'
  | 'Waiting WHP'
  | 'Waiting Countersign'
  | 'Fully Executed'
  | 'Declined'
  | 'Cancelled'
  | 'Cancelled – Resume Draft';

/**
 * The `leaseStatus` OBJECT the API attaches to each group in
 * GET /api/applications (and returns from the status/refresh routes). The
 * human-readable text is `label` (already includes "X/Y signed" and the
 * "Revision – " prefix when applicable); `status` is the enum the UI keys off.
 */
export interface LeaseStatus {
  status: LeaseStatusValue;
  /** Human-readable label for the UI (e.g. "2/3 signed", "Revision – 1/2 signed"). */
  label: string;
  /** X = adult resident submitters completed (only meaningful for 'Signing'). */
  signedCount?: number;
  /** Y = adult resident submitter count, frozen at send time. */
  totalCount?: number;
  /** True when a revision coexists with an older open submission (label is prefixed). */
  isRevision?: boolean;
}

export type LeaseStatusKind =
  | 'new'
  | 'awaiting-applicant-details'
  | 'resume-draft'
  | 'generated'
  | 'sent'
  | 'signing' // "X/Y signed"
  | 'waiting-whp'
  | 'waiting-countersign'
  | 'fully-executed'
  | 'declined'
  | 'cancelled'
  | 'cancelled-resume-draft'
  | 'revision-resume-draft'
  | 'revision-generated'
  | 'revision-signing'
  | 'unknown';

export interface LeaseStatusClass {
  kind: LeaseStatusKind;
  /** DS pill tone. */
  tone: 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';
  /** Statuses that need staff attention get visual prominence (§1). */
  actionable: boolean;
  /** Parsed "X/Y signed" counts when the status is a signing variant. */
  signed?: { signed: number; total: number };
}

/**
 * Map a `leaseStatus` OBJECT to a render classification. Keys off the `status`
 * enum (authoritative) and `isRevision`; pulls counts off signedCount/totalCount.
 * Defensive: any unrecognized status falls back to a neutral, non-actionable
 * badge so a new server status renders gracefully rather than crashing the list.
 */
export function classifyLeaseStatus(raw: LeaseStatus | null | undefined): LeaseStatusClass {
  if (!raw || !raw.status) {
    return { kind: 'unknown', tone: 'neutral', actionable: false };
  }
  const isRevision = raw.isRevision === true;
  const signed =
    typeof raw.signedCount === 'number' && typeof raw.totalCount === 'number'
      ? { signed: raw.signedCount, total: raw.totalCount }
      : undefined;

  switch (raw.status) {
    case 'New Lease':
      return { kind: 'new', tone: 'neutral', actionable: false };
    case 'Awaiting Applicant Details':
      return { kind: 'awaiting-applicant-details', tone: 'warning', actionable: true };
    case 'Resume Draft':
      return {
        kind: isRevision ? 'revision-resume-draft' : 'resume-draft',
        tone: isRevision ? 'warning' : 'neutral',
        actionable: false,
      };
    case 'Lease Generated':
      return {
        kind: isRevision ? 'revision-generated' : 'generated',
        tone: 'accent',
        actionable: false,
      };
    case 'Signing':
      return {
        kind: isRevision ? 'revision-signing' : 'signing',
        tone: 'info',
        actionable: false,
        ...(signed ? { signed } : {}),
      };
    case 'Waiting WHP':
      // WHP must act — high prominence.
      return { kind: 'waiting-whp', tone: 'warning', actionable: true };
    case 'Waiting Countersign':
      return { kind: 'waiting-countersign', tone: 'warning', actionable: true };
    case 'Fully Executed':
      return { kind: 'fully-executed', tone: 'success', actionable: false };
    case 'Declined':
      return { kind: 'declined', tone: 'danger', actionable: true };
    case 'Cancelled':
      return { kind: 'cancelled', tone: 'danger', actionable: false };
    case 'Cancelled – Resume Draft':
      return { kind: 'cancelled-resume-draft', tone: 'warning', actionable: true };
    default:
      return { kind: 'unknown', tone: 'neutral', actionable: false };
  }
}

/** The display text for a status object (label, with a defensive fallback). */
export function leaseStatusLabel(raw: LeaseStatus | null | undefined): string {
  return (raw?.label ?? raw?.status ?? '').trim() || '—';
}

// ---------------------------------------------------------------------------
// Send / Cancel / Revise / Refresh — wire shapes
// ---------------------------------------------------------------------------

export type DocumentType = 'lease' | 'adoption_addendum';

export interface TestSigner {
  name: string;
  email: string;
}

/** Lease send body — NO documentType (the lease send route is lease-only). */
export interface SendForSignatureBody {
  sendEmail?: boolean;
  /** Test-lease override — when present the send uses these signers (§5). */
  testSigners?: TestSigner[];
  /**
   * Explicit staff intent to send ANOTHER lease while the group already has a
   * live one (the durable group send-claim otherwise refuses with 409
   * `tracked_success`). Sent ONLY on the revision path — a "Revise & keep open"
   * whose regenerated version is being sent alongside the kept-open submission.
   * Never sent on the ordinary pre-send path.
   */
  confirmNewSend?: boolean;
}

/**
 * Build the send body for the two send paths. `revision` is the ONLY thing
 * that adds `confirmNewSend: true`; the ordinary pre-send body carries no such
 * key at all (the API's zod schema is `.strict()`, and the flag-OFF path
 * ignores it — but we still keep the established body byte-for-byte).
 */
export function buildSendForSignatureBody(args: {
  sendEmail: boolean;
  testSigners?: TestSigner[] | undefined;
  revision: boolean;
}): SendForSignatureBody {
  return {
    sendEmail: args.sendEmail,
    ...(args.testSigners && args.testSigners.length > 0 ? { testSigners: args.testSigners } : {}),
    ...(args.revision ? { confirmNewSend: true } : {}),
  };
}

// ---------------------------------------------------------------------------
// Newest signature record (GET /api/lease-drafts/:groupId/status
// `signatureRecord`) — the subset the UI reads to detect a SENDABLE REVISION.
//
// "Revise & keep open" flags the open submission `keptOpen` and the page
// regenerates a new PDF version, but the group's computed status keeps
// reporting the kept-open submission (e.g. 'Waiting WHP'), so the actions
// classify as in-flight and the pre-send "Send for signature" never shows.
// The revision is sendable exactly when: the newest record is still OPEN and
// keptOpen, AND the draft's latest generated version is NEWER than that
// record's version (ULIDs compare lexicographically — same rule as the API's
// newestComparator).
// ---------------------------------------------------------------------------

export interface LeaseSignatureRecordSummary {
  /** ULID of the generated PDF version this submission was sent from. */
  versionId: string;
  /** Persisted derived lease status string (computeLeaseStatus vocabulary). */
  status: string;
  /** True while this submission is kept open alongside a newer revision. */
  keptOpen?: boolean;
  /** Whether this record was cancelled (archived in DocuSeal or unsent). */
  cancelled?: boolean;
}

/** Client mirror of the API's TERMINAL_RECORD_STATUSES (leaseSignatures.ts). */
const TERMINAL_SIGNATURE_RECORD_STATUSES: ReadonlySet<string> = new Set<string>([
  'Fully Executed',
  'Declined',
  'Cancelled',
  'Cancelled – Resume Draft',
]);

/**
 * Narrow the status route's raw `signatureRecord` (typed `unknown` on the
 * client) to the fields the revision check reads. Anything malformed → null,
 * so a shape drift degrades to "no revision button", never a crash.
 */
export function pickSignatureRecordSummary(raw: unknown): LeaseSignatureRecordSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.versionId !== 'string' || !r.versionId) return null;
  return {
    versionId: r.versionId,
    status: typeof r.status === 'string' ? r.status : '',
    ...(typeof r.keptOpen === 'boolean' ? { keptOpen: r.keptOpen } : {}),
    ...(typeof r.cancelled === 'boolean' ? { cancelled: r.cancelled } : {}),
  };
}

/** Open = not cancelled and not in a terminal status (API isOpenRecord). */
export function isOpenSignatureRecord(record: LeaseSignatureRecordSummary): boolean {
  return record.cancelled !== true && !TERMINAL_SIGNATURE_RECORD_STATUSES.has(record.status);
}

/**
 * True when a regenerated revision is waiting to be sent alongside a kept-open
 * submission: newest record OPEN + keptOpen, and `latestVersionId` strictly
 * NEWER than the record's versionId. Equal versions (revise started, regenerate
 * not done / failed) → false; a newer record that is not keptOpen (the revision
 * already went out) → false.
 */
export function revisionSendReady(
  record: LeaseSignatureRecordSummary | null | undefined,
  latestVersionId: string | null | undefined,
): boolean {
  if (!record || !latestVersionId) return false;
  if (record.keptOpen !== true) return false;
  if (!isOpenSignatureRecord(record)) return false;
  return latestVersionId > record.versionId;
}

/**
 * Response shape for the lifecycle mutations. Fields vary by route; we read
 * defensively and surface `status` (the new computed status object) +
 * `missingDobNames` when present.
 */
export interface LeaseLifecycleResult {
  /** Resulting lease status object (same vocabulary as the list column). */
  status?: LeaseStatus | string;
  /** Applicants with no DOB on file — returned by the backend for observability;
   *  no longer surfaced in the UI (WEL-27: a missing DOB never blocks signing,
   *  so the old "add DOBs before they can sign" warning was misleading). */
  missingDobNames?: string[];
  /** Revise: whether the client should now regenerate + send a new version. */
  regenerate?: boolean;
  /** Revise: whether the new send coexists with an older open (keep-open). */
  keepOpen?: boolean;
  /** Optional message the API may include. */
  message?: string;
  [key: string]: unknown;
}

/** The three group-level revise actions the backend accepts. */
export type ReviseAction = 'cancel' | 'cancel-resend' | 'keep-open';

/** Backend response for POST /api/lease-drafts/:groupId/revise. */
export interface ReviseResult {
  action: ReviseAction;
  regenerate: boolean;
  keepOpen: boolean;
  cancelledSubmissionIds: number[];
  keptOpenSubmissionId?: number;
  status: LeaseStatusValue | string;
  hadSubmission?: boolean;
}

const groupPath = (groupId: string, suffix: string) =>
  `/api/lease-drafts/${encodeURIComponent(groupId)}${suffix}`;

const versionPath = (groupId: string, versionId: string, suffix: string) =>
  `/api/lease-drafts/${encodeURIComponent(groupId)}/versions/${encodeURIComponent(versionId)}${suffix}`;

/**
 * POST …/versions/:versionId/send-for-signature — lease only. No documentType
 * (the addendum has its own send route; see sendAdoptionAddendum).
 */
export function sendForSignature(
  groupId: string,
  versionId: string,
  body: SendForSignatureBody = {},
): Promise<LeaseLifecycleResult> {
  return api.post<LeaseLifecycleResult>(
    versionPath(groupId, versionId, '/send-for-signature'),
    body,
  );
}

/** POST …/versions/:versionId/cancel */
export function cancelLease(
  groupId: string,
  versionId: string,
): Promise<LeaseLifecycleResult> {
  return api.post<LeaseLifecycleResult>(versionPath(groupId, versionId, '/cancel'));
}

/**
 * POST /api/lease-drafts/:groupId/revise — GROUP-level (not version-level).
 * Body { action }. The response `regenerate` flag tells the UI to route to
 * regenerate+send (for 'cancel-resend' and 'keep-open').
 */
export function reviseLease(
  groupId: string,
  action: ReviseAction,
): Promise<ReviseResult> {
  return api.post<ReviseResult>(groupPath(groupId, '/revise'), { action });
}

/** POST …/versions/:versionId/refresh-status — manual status reconcile (§2). */
export function refreshStatus(
  groupId: string,
  versionId: string,
): Promise<LeaseLifecycleResult> {
  return api.post<LeaseLifecycleResult>(versionPath(groupId, versionId, '/refresh-status'));
}

// ---------------------------------------------------------------------------
// Adoption addendum send (§3) — separate route from the lease send.
// ---------------------------------------------------------------------------

export interface AddendumPerson {
  firstName: string;
  lastName: string;
  email?: string | null;
}

export interface SendAdoptionAddendumBody {
  /** Adoptee(s) being added — required. */
  adoptees: AddendumPerson[];
  /** Override current residents (else resolved from the tenancy's unit). */
  currentResidents?: AddendumPerson[];
  premises?: string;
  startDate?: string;
  endDate?: string;
  otherTerms?: string;
  sendEmail?: boolean;
}

/** POST /api/adoption-addendum/:groupId/send */
export function sendAdoptionAddendum(
  groupId: string,
  body: SendAdoptionAddendumBody,
): Promise<LeaseLifecycleResult> {
  return api.post<LeaseLifecycleResult>(
    `/api/adoption-addendum/${encodeURIComponent(groupId)}/send`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Came-of-age to-do queue (§4) — aligned to the backend LeaseTodoRecord.
// ---------------------------------------------------------------------------

/** Mirrors the backend LeaseTodoRecord (leaseTodos.ts). */
export interface TodoItem {
  todoId: string;
  type: string;
  status: string;
  groupId: string;
  tenancyId?: string;
  minor: {
    key: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    turned18At: string;
  };
  title: string;
  createdAt: string;
  updatedAt: string;
}

/** Convenience: the minor's display name for a to-do. */
export function todoMinorName(t: TodoItem): string {
  return `${t.minor.firstName} ${t.minor.lastName}`.trim() || 'Resident';
}

/** The prefilled addendum payload returned by start-addendum. */
export interface StartAddendumResult {
  todo: TodoItem;
  addendum: {
    groupId: string;
    tenancyId?: string;
    currentResidents: AddendumPerson[];
    adoptees: AddendumPerson[];
    premises: string;
    startDate: string;
    endDate: string;
    renderConfig: unknown;
  };
}

/** GET /api/todos → { count, todos:[...] }. */
export function listTodos(): Promise<TodoItem[]> {
  return api.get<{ count: number; todos: TodoItem[] }>('/api/todos').then((r) => r.todos ?? []);
}

/**
 * GET /api/todos → just the count of open/relevant to-dos.
 *
 * "Open/relevant" == the items the backend returns from the came-of-age queue
 * (minors who reached adulthood and still need an adoption addendum). They drop
 * off the queue once an addendum is started, so the raw list length is the
 * attention count. We read the server-computed `count` field and fall back to
 * `todos.length` if it's ever absent.
 */
export function fetchTodosCount(): Promise<number> {
  return api
    .get<{ count?: number; todos?: TodoItem[] }>('/api/todos')
    .then((r) => r.count ?? r.todos?.length ?? 0);
}

/** POST /api/todos/:id/start-addendum → { todo, addendum }. */
export function startAddendum(todoId: string): Promise<StartAddendumResult> {
  return api.post<StartAddendumResult>(
    `/api/todos/${encodeURIComponent(todoId)}/start-addendum`,
  );
}

// ---------------------------------------------------------------------------
// Test-lease tool (§5) — aligned to POST /api/lease-drafts/test-send (A3).
// ---------------------------------------------------------------------------

export interface SendTestLeaseBody {
  propertyId: string;
  testSigners: TestSigner[];
  /** lease (default) or adoption_addendum. */
  documentType?: DocumentType;
  /** Whether DocuSeal should email the test signers (default false for tests). */
  sendEmail?: boolean;
}

export interface SendTestLeaseResult {
  documentType: DocumentType;
  docusealSubmissionId: number;
  residentCount?: number;
  status: string;
  [key: string]: unknown;
}

/**
 * Send a test submission for a property with arbitrary signer names/emails.
 * Backend route (A3): POST /api/lease-drafts/test-send with the property +
 * signers + optional documentType. sendEmail defaults false (tests don't spam).
 */
export function sendTestLease(body: SendTestLeaseBody): Promise<SendTestLeaseResult> {
  return api.post<SendTestLeaseResult>('/api/lease-drafts/test-send', {
    sendEmail: false,
    ...body,
  });
}

// ---------------------------------------------------------------------------
// Properties (for the test-lease property picker). Mirrors GET /api/properties
// shape used elsewhere; kept here so this hook module has no lib/ cross-import.
// ---------------------------------------------------------------------------

export interface PropertyOption {
  propertyId: string;
  name: string;
  address: string | null;
  propertyType: string | null;
}

export function listProperties(): Promise<PropertyOption[]> {
  return api
    .get<{ properties: PropertyOption[] } | PropertyOption[]>('/api/properties')
    .then((r) => (Array.isArray(r) ? r : (r.properties ?? [])));
}

export interface UnitOption {
  unitId: string;
  name: string | null;
  address: string | null;
}


/**
 * What a unit is CALLED in a picker — never its id (docs/no-ids-in-ui.md).
 *
 * Six pages wrote `{u.name ?? u.unitId}`, so a unit AppFolio has no Name for
 * turned the option into an internal id the staffer picking a unit cannot match
 * to anything. The address is the thing they actually recognise, and a bare
 * "Unit" at least does not pretend to be one.
 */
export function unitOptionLabel(u: UnitOption): string {
  return u.name?.trim() || u.address?.trim() || 'Unit';
}

/** List the units for a property (test-lease unit picker). */
export function listUnits(propertyId: string): Promise<UnitOption[]> {
  return api
    .get<{ units: UnitOption[] } | UnitOption[]>(
      `/api/properties/${encodeURIComponent(propertyId)}/units`,
    )
    .then((r) => (Array.isArray(r) ? r : (r.units ?? [])));
}

// ---------------------------------------------------------------------------
// Test-lease launcher (§5): create a persisted test draft for a property+unit
// with dummy applicants, then open it in the full lease builder.
// ---------------------------------------------------------------------------

export interface CreateTestLeaseBody {
  propertyId: string;
  unitId: string;
  /** One per signer. `name` is a dummy display name; `email` receives signing. */
  signers: Array<{ name: string; email: string }>;
}

export interface CreateTestLeaseResult {
  groupId: string;
  [key: string]: unknown;
}

/** Create a test-lease draft and return its `test-` groupId for the builder. */
export function createTestLease(body: CreateTestLeaseBody): Promise<CreateTestLeaseResult> {
  return api.post<CreateTestLeaseResult>('/api/lease-drafts/test-create', body);
}

// ---------------------------------------------------------------------------
// Renewal launcher: send a lease to a unit's EXISTING tenants. Load the unit's
// current renewal signers (18+ occupants + cosigners), let staff edit the
// roster, then create a NORMAL editable draft and open the standard builder.
// ---------------------------------------------------------------------------

/** A current renewal signer for a unit (GET .../current-residents). */
export interface RenewalResidentOption {
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  /** Verbatim AppFolio TenantType ('Financially Responsible' | 'Co-signer'). */
  tenantType: string | null;
  /** yyyy-mm-dd; null when AppFolio has no DOB on file. */
  dateOfBirth: string | null;
}

/** A unit's current renewal signers + the resolved occupancy lease start date. */
export interface CurrentResidentsResult {
  residents: RenewalResidentOption[];
  /**
   * The occupancy's resolved current-lease START date (yyyy-mm-dd), or '' when
   * AppFolio carries none on file. The payment-plan page prefills its required
   * "Lease start date" field from this (staff can edit; blank → must enter).
   */
  leaseStartDate: string;
}

/**
 * Load a unit's current renewal signers (18+ occupants + all cosigners) AND the
 * occupancy's resolved lease start date. Tolerates the legacy bare-array / sans-
 * leaseStartDate response shapes (leaseStartDate defaults to '').
 */
export function listCurrentResidents(
  propertyId: string,
  unitId: string,
): Promise<CurrentResidentsResult> {
  return api
    .get<{ residents?: RenewalResidentOption[]; leaseStartDate?: string } | RenewalResidentOption[]>(
      `/api/properties/${encodeURIComponent(propertyId)}/units/${encodeURIComponent(unitId)}/current-residents`,
    )
    .then((r) =>
      Array.isArray(r)
        ? { residents: r, leaseStartDate: '' }
        : { residents: r.residents ?? [], leaseStartDate: r.leaseStartDate ?? '' },
    );
}

export interface CreateRenewalLeaseBody {
  propertyId: string;
  unitId: string;
  /** The (possibly edited) roster. Omit to let the backend re-derive from AppFolio. */
  residents?: RenewalResidentOption[];
}

export interface CreateRenewalLeaseResult {
  groupId: string;
  /** Which cycle branch seeded the term/rent default (telemetry). */
  termBasis?: string;
  /** AppFolio inputs that were missing and defaulted around. */
  termGaps?: string[];
  [key: string]: unknown;
}

/** Create a renewal draft and return its `renewal-` groupId for the builder. */
export function createRenewalLease(
  body: CreateRenewalLeaseBody,
): Promise<CreateRenewalLeaseResult> {
  return api.post<CreateRenewalLeaseResult>('/api/lease-drafts/renewal-create', body);
}

// ---------------------------------------------------------------------------
// Hook: useTodos — load + refresh the came-of-age queue.
// ---------------------------------------------------------------------------

export interface UseTodosResult {
  todos: TodoItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useTodos(): UseTodosResult {
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guards setState after the component unmounts (or after a newer reload
  // supersedes an in-flight one). Each reload captures the live ref value at
  // call time and bails if it has been flipped.
  const cancelledRef = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTodos();
      if (cancelledRef.current) return;
      setTodos(list);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load to-dos');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void reload();
    return () => {
      cancelledRef.current = true;
    };
  }, [reload]);

  return { todos, loading, error, reload };
}

// ---------------------------------------------------------------------------
// Hook: useTodosCount — lightweight count of open to-dos for the nav badge.
//
// Used by AppShell to draw an attention counter on the "To-dos" nav item the
// few times a year it's relevant (WEL-31). Fetches once on mount — the queue
// changes rarely (a minor coming of age), so polling would be wasted effort.
// On any error it stays at 0 so the nav never shows a bogus badge.
// ---------------------------------------------------------------------------

export function useTodosCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void fetchTodosCount()
      .then((n) => {
        if (!cancelled) setCount(n);
      })
      .catch(() => {
        // Swallow — a failed count just means no badge, never a broken nav.
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return count;
}
